import {
    API_ENDPOINTS,
    customToast,
    FormInput,
    Permissions,
    replaceKeyWithValue,
    Roles,
    route,
    SelectOption,
    Stepper,
    StringHelpers,
    useAuthorisationContext,
    useDisabledContext,
    useLangContext,
    useQueries,
    useUserContext,
    Variants,
    SubTitle,
    Text,
    Button,
    Sizes,
} from 'carrier-fe';
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { ClaimsFormData } from '../../types/claim';
import { ToshibaInstallation } from '../../types/installation';
import { UseFormReturn } from '../../types/journey';
import { QUERY_KEYS } from '../../util/Constants';
import { Address } from '../../types/shared';
import { CustomerCompanyDetails } from '../../types/customer';
import CreateCustomerModal from '../CreateCustomerModal';

type SelectKey = 'installer_companies' | 'installations' | 'customers' | 'distributors';
type Selects = Record<SelectKey, SelectOption[]>;

function ClaimsStep1({
    form,
}: {
    form: UseFormReturn<ClaimsFormData, any>;
}) {
    // Hooks and Contexts
    const { setValues, store, errors } = form;
    const { crud, fields } = useLangContext();
    const { disabled, setDisabled } = useDisabledContext();
    const { hasRoleAccess, hasPermissionAccess } = useAuthorisationContext();
    const { user } = useUserContext();

    // State Management
    const [selects, setSelects] = useState<Selects>({
        installer_companies: [],
        installations: [],
        customers: [],
        distributors: [],
    });
    const [customerSiteAddress, setCustomerSiteAddress] = useState<Address | null>(null);
    const [companyAccountNumber, setCompanyAccountNumber] = useState<string | null>(null);
    const [installationSelected, setInstallationSelected] = useState(false);
    const [showCreateCustomerModal, setShowCreateCustomerModal] = useState(false);

    // Queries
    const getQueries = () => {
        return [
            {
                key: QUERY_KEYS.INSTALLER_COMPANIES,
                config: {
                    method: 'GET',
                    url: route(`${API_ENDPOINTS.GENERIC.SELECT.COMPANIES}?types[]=installer`),
                },
            },
            ...store?.company_id ? [
                {
                    key: QUERY_KEYS.INSTALLATIONS,
                    config: {
                        method: 'GET',
                        url: route(API_ENDPOINTS.GENERIC.SELECT.INSTALLATIONS, {
                            companyId: store?.company_id,
                        }),
                    },
                },
                {
                    key: QUERY_KEYS.CUSTOMERS,
                    config: {
                        method: 'GET',
                        url: route(API_ENDPOINTS.GENERIC.SELECT.CUSTOMERS, {
                            company_id: store?.company_id,
                        }),
                    },
                },
                {
                    key: QUERY_KEYS.DISTRIBUTORS,
                    config: {
                        method: 'GET',
                        url: route(API_ENDPOINTS.GENERIC.SELECT.DISTRIBUTORS, {
                            companyId: store?.company_id,
                        }),
                    },
                },
            ] : [],
        ];
    };

    const {
        data: selectOptions,
        error,
        isLoading,
        refetch,
    } = useQueries<{ data: SelectOption[] }>(getQueries());

    // Effects

    useEffect(() => {
        if (hasRoleAccess(Roles.COMPANY_MANAGER)) {
            setValues({
                company_id: user?.individual?.company_id,
            });
        }
    }, [user]);

    useEffect(() => {
        if (!store?.company_id) return;

        refetch([
            QUERY_KEYS.INSTALLATIONS,
            QUERY_KEYS.CUSTOMERS,
            QUERY_KEYS.DISTRIBUTORS
        ]);

        const fetchCompanyDetails = async () => {
            try {
                const response = await axios.get<{ data: CustomerCompanyDetails}>(
                    route(
                        API_ENDPOINTS.REGISTRATION.CUSTOMER.VIEW,
                        { customerId: store?.company_id }
                    )
                );

                const accountNumber = response.data?.data?.account_number;
                setCompanyAccountNumber(accountNumber || null);

                if (accountNumber) {
                    setValues({ account_number: accountNumber });
                }
            } catch (err: any) {
                // Account number will need to be entered manually
                setCompanyAccountNumber(null);
            }
        };

        fetchCompanyDetails();
    }, [store?.company_id]);

    // Update selects when query data arrives
    useEffect(() => {
        if (!selectOptions) return;

        setSelects((prev) => ({
            ...prev,
            installer_companies: selectOptions[QUERY_KEYS.INSTALLER_COMPANIES]?.data ?? [],
            installations: selectOptions[QUERY_KEYS.INSTALLATIONS]?.data ?? [],
            customers: selectOptions[QUERY_KEYS.CUSTOMERS]?.data ?? [],
            distributors: selectOptions[QUERY_KEYS.DISTRIBUTORS]?.data ?? [],
        }));

        // Auto-select distributor if only one
        if (selectOptions[QUERY_KEYS.DISTRIBUTORS]?.data?.length === 1) {
            setValues({
                distributor_id: selectOptions[QUERY_KEYS.DISTRIBUTORS]?.data[0].value,
            });
        }
    }, [selectOptions]);

    // Handle query errors
    useEffect(() => {
        Object.values(error).forEach((err: any) => {
            if (!err) return;

            customToast({
                title: err?.response?.data?.message || 'An error occurred.',
                variant: Variants.Danger,
            })
        });
    }, [error]);

    // Fetch installation details when installation is selected
    useEffect(() => {
        if (!store?.toshiba_installation_id) {
            setInstallationSelected(false);
            return;
        }

        const fetchInstallationDetails = async () => {
            setDisabled(true);

            try {
                const response = await axios.get<{ data: ToshibaInstallation }>(
                    route(API_ENDPOINTS.REGISTRATION.INSTALLATION.VIEW, {
                        installationId: store.toshiba_installation_id,
                    })
                );

                const installation = response.data?.data;

                if (installation) {
                    setInstallationSelected(true);

                    // Pre-fill fields from installation
                    setValues({
                        installation_date: installation.commissioning_date || '',
                        customer_id: installation.customer_id || '',
                        distributor_id: installation.distributor_id || '',
                        product_child_id: installation.product_child_id || '',
                    });

                    // Set customer site address from installation's customer
                    if (installation.customer_company?.primary_site?.address) {
                        setCustomerSiteAddress(installation.customer_company.primary_site.address);
                    }
                }
            } catch (err: any) {
                customToast({
                    title: err?.response?.data?.message || 'Failed to load installation details.',
                    variant: Variants.Danger,
                });
                setInstallationSelected(false);
            } finally {
                setDisabled(false);
            }
        };

        fetchInstallationDetails();
    }, [store?.toshiba_installation_id]);

    // Fetch customer details when customer is selected (and not from installation)
    useEffect(() => {
        if (!store?.customer_id || installationSelected) return;

        const fetchCustomerDetails = async () => {
            setDisabled(true);

            try {
                const response = await axios.get<{ data: CustomerCompanyDetails}>(
                    route(API_ENDPOINTS.REGISTRATION.CUSTOMER.VIEW, {
                        customerId: store.customer_id,
                    })
                );

                const customer = response.data?.data;

                if (customer?.primary_site?.address) {
                    setCustomerSiteAddress(customer.primary_site.address);
                } else {
                    setCustomerSiteAddress(null);
                }
            } catch (err: any) {
                customToast({
                    title: err?.response?.data?.message || 'Failed to load customer details.',
                    variant: Variants.Danger,
                });
                setCustomerSiteAddress(null);
            } finally {
                setDisabled(false);
            }
        };

        fetchCustomerDetails();
    }, [store?.customer_id, installationSelected]);

    // Derived States
    const disableCompanyField = useMemo(
        () => hasRoleAccess(Roles.COMPANY_MANAGER) && !!store?.company_id,
        [store?.company_id]
    );

    const disableAccountNumber = useMemo(
        () => !!companyAccountNumber,
        [companyAccountNumber]
    );

    const disableInstallationDate = useMemo(
        () => installationSelected && !!store?.installation_date,
        [installationSelected, store?.installation_date]
    );

    const disableCustomer = useMemo(
        () => installationSelected && !!store?.customer_id,
        [installationSelected, store?.customer_id]
    );

    const disableDistributor = useMemo(
        () => installationSelected && !!store?.distributor_id,
        [installationSelected, store?.distributor_id]
    );

    // Render address display
    const renderSiteAddress = () => {
        if (!customerSiteAddress) return null;

        const addressParts = [
            customerSiteAddress.address_line_1,
            customerSiteAddress.address_line_2,
            customerSiteAddress.address_line_3,
            customerSiteAddress.town_city,
            customerSiteAddress.state_county,
            customerSiteAddress.postcode_zipcode,
            customerSiteAddress.country,
        ].filter(Boolean);

        if (addressParts.length === 0) return null;

        return (
            <div className='mb-4 p-3 bg-light rounded'>
                <strong className="d-block mb-2">
                    {StringHelpers.title(fields?.address || 'Address')}
                </strong>
                <div className='text-muted'>
                    {addressParts.map((part, index) => (
                        <span key={index}>
                            {part}
                            {index < addressParts.length - 1 && ', '}
                        </span>
                    ))}
                </div>
            </div>
        );
    };

    return (
        <Stepper.Step
            loading={isLoading}
        >
            {/* Company Details Section */}

            <SubTitle 
                title={StringHelpers.title(`${crud?.sub_titles?.company || 'Company'} ${crud?.sub_titles?.details || 'Details'}`)}
            />

            <FormInput
                name='company_id'
                type='searchable-select'
                label={StringHelpers.title(crud?.models?.company || 'Company')}
                disabled={disabled || disableCompanyField}
                options={selects.installer_companies}
                value={store?.company_id}
                onChange={(value) => {
                    if (typeof value !== 'string') return;
                    setValues({ company_id: value });
                }}
                errorMessages={errors?.company_id}
                clearable={false}
            />

            <FormInput
                name='account_number'
                type='text'
                label={StringHelpers.title(fields?.account_number || 'Account Number')}
                disabled={disabled || disableAccountNumber}
                value={store?.account_number}
                onChange={(value) => {
                    if (typeof value !== 'string') return;
                    setValues({ account_number: value });
                }}
                errorMessages={errors?.account_number}
            />

            {/* Installation Details Section */}
            
            <SubTitle 
                title={StringHelpers.title(`${crud?.models?.installation || 'Installation'} ${crud?.sub_titles?.details || 'Details'}`)}
            />
            
            <FormInput
                name='toshiba_installation_id'
                type='searchable-select'
                label={StringHelpers.title(crud?.models?.installation || 'Installation')}
                disabled={disabled}
                options={selects.installations}
                value={store?.toshiba_installation_id}
                onChange={(value) => {
                    if (!value) {
                        setValues({
                            toshiba_installation_id: '',
                            installation_date: '',
                            customer_id: '',
                            distributor_id: '',
                        });
                        setInstallationSelected(false);
                        setCustomerSiteAddress(null);
                    } else {
                        if (typeof value === 'string')
                        setValues({ toshiba_installation_id: value });
                    }
                }}
                errorMessages={errors?.toshiba_installation_id}
                clearable={true}
            />

            <FormInput
                name='installation_date'
                type='datetime'
                label={StringHelpers.title(fields?.installation_date || 'Installation Date')}
                disabled={disabled || disableInstallationDate}
                value={store?.installation_date}
                onChange={(value) => {
                    if (typeof value !== 'string') return;
                    setValues({ installation_date: value });
                }}
                errorMessages={errors?.installation_date}
            />

            {/* Customer Details Section */}

            <SubTitle 
                title={StringHelpers.title(`${crud?.models?.customer || 'Customer'} ${crud?.sub_titles?.details || 'Details'}`)}
            />
                
            <div className='d-flex align-items-end gap-2 mb-2'>
                <div className='flex-grow-1'>
                    <FormInput
                        name='customer_id'
                        type='searchable-select'
                        label={StringHelpers.title(crud?.models?.customer || 'Customer')}
                        disabled={disabled || disableCustomer}
                        options={selects.customers}
                        value={store?.customer_id}
                        onChange={(value) => {
                            if (typeof value !== 'string') return;

                            if (!value) {
                                setValues({ customer_id: '' });
                                setCustomerSiteAddress(null);
                            } else {
                                setValues({ customer_id: value });
                            }
                        }}
                        errorMessages={errors?.customer_id}
                        clearable={false}
                        noMargin
                    />
                </div>
            </div>

            {hasPermissionAccess(Permissions.CUSTOMER_STORE) && !disableCustomer && (
                <div className='mb-3'>
                    <Text className='text-muted'>
                        {crud?.helpers?.customer_not_in_list ||
                            'If the customer is not appearing in the list and is a new customer, please click the button below to create a new customer record.'
                        }
                    </Text>
                    
                    <div className='mb-3'>
                        <Button
                            label={replaceKeyWithValue(
                                crud?.pages?.create?.title || 'Create :model',
                                'model',
                                crud?.models?.customer || 'Customer'
                            )}
                            size={Sizes.Small}
                            onClick={() => setShowCreateCustomerModal(true)}
                        />
                    </div>

                    <CreateCustomerModal
                        open={showCreateCustomerModal}
                        onClose={() => setShowCreateCustomerModal(false)}
                        installerCompanyId={store?.company_id || ''}
                        onCustomerCreated={(customer) => {
                            setValues({ customer_id: customer.id });
                            refetch([QUERY_KEYS.CUSTOMERS]);
                        }}
                    />
                </div>
            )}

            {renderSiteAddress()}

            {/* Distributor Details Section */}

            <SubTitle
                title={StringHelpers.title(`${crud?.sub_titles?.distributor || 'Distributor'} ${crud?.sub_titles?.details || 'Details'}`)}
            />

            <FormInput
                name='distributor_id'
                type='searchable-select'
                label={StringHelpers.title(crud?.sub_titles?.distributor || 'Distributor')}
                disabled={disabled || disableDistributor}
                options={selects.distributors}
                value={store?.distributor_id}
                onChange={(value) => {
                    if (typeof value !== 'string') return;
                    setValues({ distributor_id: value });
                }}
                errorMessages={errors?.distributor_id}
                clearable={false}
            />
        </Stepper.Step>
    );
}

export default ClaimsStep1;
